import {
    Button,
    Checkbox,
    CircularProgress,
    Grid,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, orderBy, set } from "lodash";
import moment from "moment";
import React from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import BSInput from "../../../common/js/components/BSInput";
import StatusChip from "../../../common/js/components/StatusChip";
import { Play, Search, Sorty } from "../../../common/js/icons";
import "../../styles/voucherDashboard.css";
import { aggregateBrokerNames } from "../utils/operations";
import schema from "./voucherTables";

const StyledTab = styled(Tab)(() => ({
    textTransform: "none",
}));

const PageToolbar = styled(Grid)(() => ({
    marginBottom: "20px",
    marginTop: "20px",
}));

const ListingIdLink = styled("span")({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: 600,
});

const AlternateContent = styled("div")({
    margin: "auto",
    textAlign: "center",
    marginTop: "4em",
    marginBottom: "1em",
});

const TooltipStyled = styled("span")({
    "&>a:hover": {
        cursor: "pointer",
    },
});

const ProcessLink = styled("span")({
    "&:hover": {
        cursor: "pointer",
        color: "#2C99D2",
    },
    color: "#595959",
    textDecoration: "underline",
});

const UndoProcess = styled("span")({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#595959",
    fontSize: "80%",
    marginLeft: "3px",
});

const Sortable = styled("span")({
    paddingRight: "5px",
    paddingBottom: "3px",
});

const ApplyFilterButton = styled(Button)({
    marginRight: "10px",
    marginLeft: "0px",
});

const DateFilterSelector = styled(Grid)({
    flexGrow: "inherit",
    width: "11em",
    marginBottom: "25x",
    marginLeft: "5px",
    marginRight: "5px",
});

const CheckboxCell = styled(TableCell)({
    paddingLeft: "0px",
});

const AccountingVouchers = (props) => {
    const {
        accountingBatchViewInputParams,
        columnToSort,
        elementsSaving,
        errors,
        filterQuery,
        generateBatch,
        handleApplyDateRangeFilter,
        handleChangeAccountingInputParam,
        handleCheckboxClick,
        handleFilter,
        handleSelectAll,
        handleSort,
        history,
        isLoading,
        reopenVouchers,
        selectedBoxes,
        sortDirection,
        switchVoucherView,
        togglePrintedBy,
        toggleProcessedBy,
        userProfile,
        vouchers,
        voucherView,
    } = props;

    let voucherz = get(vouchers, voucherView, []);
    const selected = get(selectedBoxes, voucherView, []);
    const sortFunction = get(
        get(schema, `viewHeaders.${voucherView}`).find((x) => x.prop === columnToSort),
        "sortFunction"
    );

    if (sortFunction && voucherz) {
        voucherz = voucherz.map((voucher) => {
            set(voucher, columnToSort, sortFunction(get(voucher, columnToSort)));
            return voucher;
        });
    }

    const data = orderBy(
        filterQuery ? voucherz.filter((x) => schema.filterByQuery(x, schema.columnsToQuery, filterQuery)) : voucherz,
        [
            (v) => {
                return get(v, columnToSort) || "";
            },
        ],
        sortDirection
    );

    return (
        <div>
            <PageToolbar container justifyContent="space-between" alignItems="center" spacing={16}>
                <Grid item>
                    <h2 className="page-title">Vouchers</h2>
                    <p className="page-subtitle">
                        Showing {voucherz ? voucherz.length : 0}{" "}
                        {!voucherz || voucherz.length > 1 || voucherz.length === 0 ? "vouchers" : "voucher"}
                    </p>
                </Grid>
                <Grid item>
                    {voucherView === "accounting_ready_payment" && (
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={userProfile.role !== "accounting"}
                            onClick={generateBatch}
                        >
                            <Play fill="#ffffff" />
                            Generate Batch Report
                        </Button>
                    )}
                    {voucherView === "accounting_closed" && (
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={userProfile.role !== "accounting"}
                            onClick={reopenVouchers}
                        >
                            Un-batch Selected Vouchers
                        </Button>
                    )}
                </Grid>
            </PageToolbar>
            <div style={{ display: "flex" }}>
                <div style={{ flex: "1 1 auto" }}>
                    <div className="simPaper">
                        <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                            <Grid item>
                                <Tabs value={voucherView} onChange={switchVoucherView}>
                                    <StyledTab value="accounting_awaiting_payment" label="Awaiting Payment" />
                                    <StyledTab value="accounting_ready_payment" label="Ready For Payment" />
                                    <StyledTab value="accounting_closed" label="Batched Vouchers" />
                                </Tabs>
                            </Grid>
                            {voucherView === "accounting_closed" && (
                                <React.Fragment>
                                    <Grid item>
                                        <Grid container alignItems="center" spacing={0} justifyContent="flex-end">
                                            <Grid item align="center">
                                                <div style={{ color: "grey" }}>Start / End Date: </div>
                                            </Grid>
                                            <DateFilterSelector item>
                                                <BSInput
                                                    id="startDate"
                                                    type={"date"}
                                                    value={get(accountingBatchViewInputParams, "startDate")}
                                                    errorText={get(errors, `startDate`, null)}
                                                    error={get(errors, `startDate`, null)}
                                                    onChange={handleChangeAccountingInputParam}
                                                    placeholder="Start Date"
                                                />
                                            </DateFilterSelector>
                                            <Grid item align="center">
                                                <div style={{ color: "grey" }}>&mdash;</div>
                                            </Grid>
                                            <DateFilterSelector item>
                                                <BSInput
                                                    id="endDate"
                                                    errorText={get(errors, `endDate`, null)}
                                                    type={"date"}
                                                    value={get(accountingBatchViewInputParams, "endDate")}
                                                    error={get(errors, `endDate`, null)}
                                                    onChange={handleChangeAccountingInputParam}
                                                    placeholder="End Date"
                                                />
                                            </DateFilterSelector>
                                            <Grid item>
                                                <ApplyFilterButton
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={handleApplyDateRangeFilter}
                                                >
                                                    Apply Filter
                                                </ApplyFilterButton>
                                            </Grid>
                                            <Grid item>
                                                <BSInput
                                                    value={filterQuery}
                                                    placeholder="Search vouchers"
                                                    startAdornment={<Search />}
                                                    onChange={(event) => {
                                                        handleFilter(event.target.value);
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </React.Fragment>
                            )}
                        </Grid>
                        <Grid container spacing={0}>
                            <Grid style={{ flex: "1 1 auto" }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            {voucherView !== "accounting_awaiting_payment" && (
                                                <CheckboxCell>
                                                    <Checkbox
                                                        indeterminate={
                                                            selected.length > 0 && selected.length < voucherz.length
                                                        }
                                                        checked={selected.length === voucherz.length}
                                                        value={selected.length === voucherz.length}
                                                        onChange={(e) => handleSelectAll(e, voucherView)}
                                                        color="primary"
                                                    />
                                                </CheckboxCell>
                                            )}
                                            {schema.viewHeaders[voucherView].map((x, i) => (
                                                <TableCell align={x.numeric ? "right" : "left"} key={`thc-${i}`}>
                                                    <div
                                                        style={{
                                                            alignItems: "center",
                                                        }}
                                                        onClick={() => handleSort(x.sort ? x.sort : x.prop)}
                                                    >
                                                        <div style={{ display: "inline-block" }}>
                                                            <Sortable>{x.name}</Sortable>
                                                            {x.name && (
                                                                <div style={{ display: "inline-block" }}>
                                                                    <Sorty />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {!elementsSaving[voucherView] &&
                                            data.map((x, index) =>
                                                row(
                                                    x,
                                                    index,
                                                    selected,
                                                    isLoading,
                                                    voucherView,
                                                    handleCheckboxClick,
                                                    toggleProcessedBy,
                                                    elementsSaving,
                                                    togglePrintedBy,
                                                    history
                                                )
                                            )}
                                    </TableBody>
                                </Table>
                            </Grid>
                        </Grid>
                        {elementsSaving[voucherView] ? (
                            <AlternateContent>
                                <CircularProgress color="primary" />
                            </AlternateContent>
                        ) : data.length > 0 ? null : (
                            <AlternateContent>Nothing to show.</AlternateContent>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const row = (
    x,
    index,
    selected,
    isLoading,
    voucherView,
    handleCheckboxClick,
    toggleProcessedBy,
    elementsSaving,
    togglePrintedBy,
    history
) => {
    return isLoading ? null : (
        <TableRow key={`tr-${index}`} selectable="false">
            {voucherView !== "accounting_awaiting_payment" && (
                <CheckboxCell>
                    <Checkbox
                        id={`ch-${index}`}
                        checked={selected.indexOf(`ch-${index}`) !== -1}
                        onChange={() => handleCheckboxClick(`ch-${index}`, voucherView, get(x, "voucherKey"))}
                        color="primary"
                    />
                </CheckboxCell>
            )}
            {schema.viewHeaders[voucherView].map((y, k) =>
                y.name === "Deal" ? (
                    <TableCell key={`trc-${k}`}>
                        <ListingIdLink
                            onClick={() => determineRedirectRoute(x, history)}
                            data-testid="dealNameAndAddress"
                        >
                            {get(x, "dealKey")}{" "}
                            {get(x, "listingName", null) !== null
                                ? ` | ${get(x, "listingName")}`
                                : get(x, "consultingDealName", null) !== null
                                ? ` | ${get(x, "consultingDealName")}`
                                : ""}
                        </ListingIdLink>
                        <div>{get(x, "address1")}</div>
                    </TableCell>
                ) : y.prop === "voucherKey" ? (
                    <TableCell key={`trc-${k}`}>
                        <Link to={`/voucher/${get(x, y.prop)}`} style={{ textDecoration: "none" }}>
                            <ListingIdLink>{get(x, y.prop)}</ListingIdLink>
                        </Link>
                    </TableCell>
                ) : y.prop === "brokers" && !elementsSaving[voucherView] ? (
                    <TableCell key={`trc-${k}`}>
                        <TooltipStyled
                            data-tooltip-id={`accounting-voucher-tooltip-${k}`}
                            data-tooltip-content={get(x, "brokers", "")}
                        >
                            {aggregateBrokerNames(get(x, "brokers"))}
                        </TooltipStyled>
                        <Tooltip id={`accounting-voucher-tooltip-${k}`} place="top" type="dark" effect="solid" />
                    </TableCell>
                ) : y.name === "Processed By" ? (
                    <TableCell style={{ textAlign: "left" }} key={`trc-${k}`} data-testid="processVoucherText">
                        {get(x, y.prop) ? (
                            <React.Fragment>
                                <div style={{ fontWeight: 600 }}>{get(x, y.prop)}</div>
                                <span>{moment(get(x, "acceptedDate")).format("M-D-YYYY h:mm a")}</span>
                                <UndoProcess onClick={() => toggleProcessedBy(true, get(x, "voucherKey"), voucherView)}>
                                    (undo)
                                </UndoProcess>
                            </React.Fragment>
                        ) : (
                            <ProcessLink onClick={() => toggleProcessedBy(false, get(x, "voucherKey"), voucherView)}>
                                Process
                            </ProcessLink>
                        )}
                    </TableCell>
                ) : y.name === "Printed By" ? (
                    <TableCell style={{ textAlign: "left" }} key={`trc-${k}`}>
                        {get(x, y.prop) ? (
                            <React.Fragment>
                                <div>Printed by {get(x, y.prop)}</div>
                                <UndoProcess
                                    onClick={() => togglePrintedBy(true, get(x, "voucherKey"), voucherView)}
                                    data-testid="printVoucherText"
                                >
                                    (undo)
                                </UndoProcess>
                            </React.Fragment>
                        ) : (
                            <ProcessLink
                                onClick={() => togglePrintedBy(false, get(x, "voucherKey"), voucherView)}
                                data-testid="printVoucherText"
                            >
                                Print
                            </ProcessLink>
                        )}
                    </TableCell>
                ) : y.transform ? (
                    <TableCell key={`trc-${k}`} align={y.numeric ? "right" : "left"}>
                        {y.transform(get(x, y.prop))}
                    </TableCell>
                ) : y.prop === "voucherStatus" ? (
                    <TableCell>
                        <StatusChip status={get(x, y.prop)} />
                    </TableCell>
                ) : (
                    <TableCell key={`trc-${k}`} align={y.numeric ? "right" : "left"}>
                        {get(x, y.prop)}
                    </TableCell>
                )
            )}
        </TableRow>
    );
};

const determineRedirectRoute = (voucher, history) => {
    if (voucher.transactionType === "Consulting" && voucher.voucherKey) {
        history.push(`/voucher/${voucher.voucherKey}`);
    } else {
        history.push(`/deal/${voucher.dealKey}`);
    }
};

export default AccountingVouchers;
